import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import i18n, { SUPPORTED_LOCALES } from "../i18n";

/**
 * Sélecteur FR/EN compact pour le header. Utilisé aussi dans la page
 * /profile (même composant, juste taille différente).
 *
 * Comportement :
 *   - Connecté : PATCH /auth/me/preferences via `updateLocale` (route
 *     `AuthContext`). La nouvelle locale est persistée côté serveur,
 *     `i18n.changeLanguage` appliqué immédiatement.
 *   - Anonyme (écran de login) : pas d'API, juste
 *     `i18n.changeLanguage` (qui persiste en localStorage via le
 *     LanguageDetector).
 *
 * Affiche un état "saving…" pendant le PATCH pour éviter qu'un double
 * clic ne pile deux requêtes (race condition idempotente côté backend
 * mais inutile).
 */
export default function LanguageSwitcher({ compact = false }) {
  const { t } = useTranslation("common");
  const { user, updateLocale } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const current = i18n.language?.startsWith("fr") ? "fr" : "en";

  const onChange = async (next) => {
    if (next === current || saving) return;
    setError(null);
    if (!user) {
      // Anonymous : juste i18next.
      i18n.changeLanguage(next);
      return;
    }
    setSaving(true);
    try {
      await updateLocale(next);
    } catch (e) {
      setError(e?.response?.data?.error || "save_failed");
      // On annule visuellement le switch en restant sur l'ancienne langue —
      // le <select> contrôlé re-render avec i18n.language qui n'a pas changé.
    } finally {
      setSaving(false);
    }
  };

  return (
    <span
      className={`inline-flex items-center gap-1 ${compact ? "text-xs" : "text-sm"}`}
      title={t("language")}
    >
      {!compact && <Languages size={14} aria-hidden="true" />}
      <label className="sr-only" htmlFor="ward3-lang-select">
        {t("language")}
      </label>
      <select
        id="ward3-lang-select"
        aria-label={t("language")}
        value={current}
        disabled={saving}
        onChange={(e) => onChange(e.target.value)}
        style={{
          fontSize: compact ? "0.7rem" : "0.8rem",
          // Préserve la densité en status bar : le padding global des <select>
          // (0.375rem 2rem …) est trop grand pour ce widget compact. On garde
          // juste la place du chevron à droite (~1.65rem).
          ...(compact && { padding: "0.15rem 1.65rem 0.15rem 0.55rem" }),
        }}
      >
        {SUPPORTED_LOCALES.map((l) => (
          <option key={l} value={l} style={{ background: "var(--color-bg)" }}>
            {l === "fr" ? t("french") : t("english")}
          </option>
        ))}
      </select>
      {error && (
        <span role="alert" className="text-red-400 text-xs ml-1">
          ⚠
        </span>
      )}
    </span>
  );
}
